import {
  FC,
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useOrganization } from "~hooks";

export interface CookiePreferences {
  essential: boolean;
  marketing: boolean;
  analytics: boolean;
}

interface CookieConsentContextType {
  preferences: CookiePreferences;
  setPreferences: (preferences: CookiePreferences) => void;
  hasSetPreferences: boolean;
  isMarketingAllowed: boolean;
  isAnalyticsAllowed: boolean;
}

interface CookieConsentProviderProps {
  children: ReactNode;
}

const defaultPreferences: CookiePreferences = {
  essential: true,
  marketing: false,
  analytics: false,
};

const CookieConsentContext = createContext<CookieConsentContextType>({
  preferences: defaultPreferences,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setPreferences: () => {},
  hasSetPreferences: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  isMarketingAllowed: false,
  isAnalyticsAllowed: false,
});

export const useCookieConsent = () => useContext(CookieConsentContext);

export const CookieConsentProvider: FC<CookieConsentProviderProps> = ({
  children,
}) => {
  const [preferences, setPreferencesState] = useState<CookiePreferences>(
    defaultPreferences
  );
  const [hasSetPreferences, setHasSetPreferences] = useState<boolean>(false);
  const { organization } = useOrganization();

  useEffect(() => {
    try {
      const storedPreferences = localStorage.getItem("cookiePreferences");
      if (storedPreferences) {
        const parsedPreferences = JSON.parse(storedPreferences);
        setPreferencesState(parsedPreferences as CookiePreferences);
        setHasSetPreferences(true);
      }
    } catch (error) {
      setPreferencesState(defaultPreferences);
      setHasSetPreferences(false);
      localStorage.removeItem("cookiePreferences");
      console.error("Error loading cookie preferences:", error);
    }
  }, []);

  const setPreferences = (newPreferences: CookiePreferences) => {
    try {
      setPreferencesState(newPreferences);
      setHasSetPreferences(true);
      localStorage.setItem("cookiePreferences", JSON.stringify(newPreferences));
    } catch (error) {
      setPreferencesState(defaultPreferences);
      setHasSetPreferences(false);
      localStorage.removeItem("cookiePreferences");
      console.error("Error saving cookie preferences:", error);
    }
  };

  const cookieConsentEnabled = Boolean(organization?.cookieConsent);

  const isMarketingAllowed = cookieConsentEnabled
    ? preferences.marketing
    : true;
  const isAnalyticsAllowed = cookieConsentEnabled
    ? preferences.analytics
    : true;

  const value = {
    preferences,
    setPreferences,
    hasSetPreferences,
    isMarketingAllowed,
    isAnalyticsAllowed,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};
